// ===== 多设备同步的非敏感配置 =====
// 认证凭据来自当前登录会话；这里永远不保存共享密钥。

export interface SyncConfig {
  enabled: boolean;
  serverUrl: string;
  deviceName: string;
}

const CONFIG_KEY = 'changfa_sync_config_v1';

/**
 * 正式服务器地址（阿里云）。2026-09-17 迁移自香港旧站 cc.cd。
 * ⚠️ 换域名时只需改这一处 + LEGACY_HOST，老设备的 localStorage 会自动跟着迁移。
 */
const CANONICAL_SERVER_URL = 'https://www.changfaxianghang.top';
const FALLBACK_SERVER_URL = CANONICAL_SERVER_URL;

/** 旧站域名（香港）。命中则自动改写为正式地址，实现"发新版自动带过去"。 */
const LEGACY_HOST = 'changfaxianghang.cc.cd';

function defaultServerUrl(): string {
  return typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : FALLBACK_SERVER_URL;
}

/** 把指向旧站的配置自动改写为正式地址；用户自定义的第三方地址原样保留 */
function migrateLegacyServerUrl(url: string): string {
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (host === LEGACY_HOST || host.endsWith(`.${LEGACY_HOST}`)) return CANONICAL_SERVER_URL;
  } catch {
    // 非法 URL，原样保留交给后续校验
  }
  return url;
}

export function normalizeSyncConfig(value: unknown): SyncConfig | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const serverUrl = migrateLegacyServerUrl(
    String(raw.serverUrl || '').trim().replace(/\/+$/, ''),
  );
  if (!serverUrl) return null;
  return {
    enabled: raw.enabled !== false,
    serverUrl,
    deviceName: String(raw.deviceName || '当前设备').trim() || '当前设备',
  };
}

export function getSyncConfig(): SyncConfig | null {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    const cfg = normalizeSyncConfig(parsed);
    if (cfg && JSON.stringify(parsed) !== JSON.stringify(cfg)) saveSyncConfig(cfg);
    return cfg;
  } catch {
    return null;
  }
}

export function saveSyncConfig(cfg: SyncConfig): void {
  localStorage.setItem(CONFIG_KEY, JSON.stringify(normalizeSyncConfig(cfg)));
}

export function ensureSyncConfig(): { defaulted: boolean; cfg: SyncConfig | null } {
  const existing = getSyncConfig();
  if (existing) return { defaulted: false, cfg: existing };
  const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
  const cfg: SyncConfig = {
    enabled: true,
    serverUrl: defaultServerUrl(),
    deviceName: isMobile ? '手机' : '电脑',
  };
  saveSyncConfig(cfg);
  return { defaulted: true, cfg };
}
